import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { motion } from "framer-motion";

/** Auto-seeded places grid — AI-generated blurbs, risks, species per city. */
export default function PlacesGallery() {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    // Kick off idempotent seed; UI shows whatever is already stored.
    http.post("/places/seed").catch(() => {});
    const load = () => http.get("/places?limit=40").then(r => setPlaces(r.data.places || [])).finally(() => setLoading(false));
    load();
    // Poll once after ~8s to pick up newly seeded rows (best-effort)
    const t = setTimeout(load, 8000);
    return () => clearTimeout(t);
  }, []);

  return (
    <section data-testid="places-section" className="py-24 md:py-32 px-6 md:px-10 max-w-[1600px] mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-10 mb-14">
        <div className="md:col-span-5">
          <div className="font-mono-tech text-[10px] tracking-[0.4em] uppercase text-[color:var(--np-muted)] mb-4">/ Places · Written by AI, checked by science</div>
          <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tighter">Twenty cities.<br/><em className="text-[color:var(--np-brand)]">Twenty climates.</em></h2>
        </div>
        <p className="md:col-span-6 md:col-start-7 self-end text-[color:var(--np-text-2)] text-lg">
          NaturePulse continuously seeds itself with AI-written climate profiles for cities worldwide — biome, key risks, and native species — grounded in mainstream science.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-black/10">
        {loading ? (
          Array.from({ length: 9 }).map((_, i) => <div key={i} className="bg-[color:var(--np-bg)] h-56 animate-pulse" />)
        ) : places.length === 0 ? (
          <div className="col-span-3 bg-[color:var(--np-bg)] p-10 text-center text-[color:var(--np-text-2)]">Seeding places… check back in a moment.</div>
        ) : (
          places.map((p, i) => (
            <motion.button
              key={p.name + i}
              onClick={() => setOpen(p)}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-10%" }}
              transition={{ duration: 0.5, delay: i * 0.03 }}
              data-testid={`place-card-${i}`}
              className="bg-[color:var(--np-bg)] p-8 text-left hover:bg-[color:var(--np-bg-2)] transition-colors group"
            >
              <div className="font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-brand)] mb-2">{p.country}</div>
              <div className="font-serif-display text-3xl leading-tight tracking-tight mb-3 group-hover:text-[color:var(--np-brand)] transition-colors">{p.name}</div>
              <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase text-[color:var(--np-muted)] mb-3">
                {p.lat.toFixed(2)}° · {p.lon.toFixed(2)}° · {p.biome}
              </div>
              <p className="text-sm text-[color:var(--np-text-2)] line-clamp-3">{p.blurb}</p>
            </motion.button>
          ))
        )}
      </div>

      {open && (
        <div className="fixed inset-0 z-[80] bg-black/60 backdrop-blur-sm flex items-start md:items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(null)}>
          <motion.article
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="max-w-2xl w-full bg-[color:var(--np-bg)] border border-black/10 p-8 md:p-10 my-10"
            onClick={(e) => e.stopPropagation()}
            data-testid="place-modal"
          >
            <button onClick={() => setOpen(null)} className="float-right font-mono-tech text-[10px] tracking-[0.3em] uppercase text-[color:var(--np-text-2)] hover:text-[color:var(--np-text)]" data-testid="place-close-btn">Close ✕</button>
            <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-2">{open.country} · {open.biome}</div>
            <h3 className="font-serif-display text-5xl leading-none tracking-tight mb-4">{open.name}</h3>
            <div className="font-mono-tech text-[11px] tracking-[0.25em] uppercase text-[color:var(--np-muted)] mb-6">
              {open.lat.toFixed(4)}° · {open.lon.toFixed(4)}°
            </div>
            <p className="text-[color:var(--np-text-2)] text-lg leading-relaxed mb-8">{open.blurb}</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-3">Key risks</div>
                <ul className="space-y-2">
                  {(open.risks || []).map((r, k) => <li key={k} className="font-serif-display text-lg leading-snug flex items-baseline gap-2"><span className="w-1 h-1 rounded-full bg-[color:var(--np-alert)]" />{r}</li>)}
                </ul>
              </div>
              <div>
                <div className="font-mono-tech text-[10px] tracking-[0.35em] uppercase text-[color:var(--np-brand)] mb-3">Notable species</div>
                <ul className="space-y-2">
                  {(open.species || []).map((s, k) => <li key={k} className="font-serif-display text-lg leading-snug flex items-baseline gap-2"><span className="w-1 h-1 rounded-full bg-[color:var(--np-live)]" />{s}</li>)}
                </ul>
              </div>
            </div>
          </motion.article>
        </div>
      )}
    </section>
  );
}
